/**
 * F 146 B — the scoped credential store.
 *
 * BUILT ON WHAT ALREADY EXISTED
 * -----------------------------
 * `freshcards/credentials` already ships a store that encrypts with the
 * session key, keeps `CredentialRecord` in IndexedDB, and exposes
 * `list() -> CredentialMeta[]` (metadata only). That is not nothing, and
 * rebuilding it would have been the wrong move. This adds the three things
 * it lacked:
 *
 *   1. A SCOPING MODEL — a principal. The old store keyed everything by a bare
 *      `name`, so a user credential and an app credential with the same name
 *      would collide, and there was no way to say whose a credential was.
 *   2. A `credential` ENTITY KIND, so credentials are first-class in the
 *      taxonomy rather than an untyped bag.
 *   3. A clear PLAINTEXT BOUNDARY — one function that returns plaintext, and
 *      it is not the one consumers are meant to call.
 *
 * THE PRINCIPAL QUESTION (asked, not assumed)
 * -------------------------------------------
 * The dispatch listed per-user, per-app AND per-operator scoping and said not
 * to commit until the shape was defensible. Here it is:
 *
 *   A principal is a (scope, id) pair.   scope: 'user' | 'app' | 'operator'
 *   Credential ids are `${scope}:${id}:${provider}:${name}`.
 *
 *   F 328 — THE PROVIDER IS PART OF THE ID.
 *   -----------------------------------------
 *   This used to be `${scope}:${id}:${name}`, with no provider, and it was a
 *   collision waiting to happen the moment a second provider existed: a Groq
 *   `api-key` and a Gemini `api-key` under the same principal produced the SAME
 *   record id, so the second `set()` silently overwrote the first and the
 *   pipeline lost a key. `SetInput` already carried `provider` — `qualify()`
 *   simply threw it away.
 *
 *   Because every StoredCredential ALSO stores `name` and `provider` as real
 *   fields, the legacy id shape is not load-bearing: nothing parses a credential
 *   id back into parts, and `credentialFcid()` is a plain concatenation. So a
 *   read that misses on the new id falls back to a FIELD-BASED scan, which finds
 *   legacy records whatever their id looks like, and an explicit idempotent
 *   `migrateCredentialIds()` converges a real browser store.
 *
 *   Gemini's verdict was DESIGN 3 (both), and its sharpest edge case is the one
 *   that shapes `set()`: writing a new-format record while a legacy record for
 *   the same (principal, name, provider) still exists creates TWO logical
 *   records under different keys. So `set()` MOVES the legacy record — it
 *   carries `createdAt` across and deletes the old key — instead of shadowing it.
 *
 * Why one model with three scopes beats three separate stores: a bridge
 * resolving a provider credential has to ask ONE question ("give me the
 * effective secret for provider X in this context") and the store decides
 * which principal answers. With three stores it would have to know which
 * store to ask, and that knowledge would leak into every call site.
 *
 * The ordering below is a real decision, not decoration: an explicit request
 * beats a context default, and a context default beats ambient. Ambient last
 * is what makes "a credential exists" not mean "a credential is exposed".
 */

import { encrypt, decrypt, deriveKeyWithKdf, type KdfId } from '../../crypto';

// ── the model ──────────────────────────────────────────────────────────────

export type CredentialScope = 'user' | 'app' | 'operator';

export type CredentialProvider =
  | 'groq' | 'gemini' | 'nvidia' | 'minimax' | 'github' | 'pocketbase' | (string & {});

/** What kind of secret this is — drives policy, not just display. */
export type CredentialKind = 'api_key' | 'oauth_token' | 'basic_auth' | 'signing_key' | (string & {});

/** A principal: whose credential this is. */
export interface Principal {
  scope: CredentialScope;
  id: string;
  /**
   * F 313 — the tenant identity a caller reads AS.
   *
   * Added here rather than in `identity.ts` because `Principal` is the type the
   * credential store already speaks: `KeyResolver` takes one, and every
   * consumer downstream of encryption needs an email to read. Keeping it off
   * the base shape meant `getEmailForReq` had nothing to read and fell back to
   * admin (FVC 803).
   *
   * OPTIONAL at the type level and REQUIRED in practice: `isPrincipal` refuses
   * a principal with no id or no scope, and `hasEmail` is the separate test a
   * tenant read must pass. A principal without an email is real but cannot read
   * tenant-scoped data — it is not silently promoted to a default.
   */
  email?: string;
  /** Optional display name. Never used for authorisation. */
  name?: string;
}

export interface CredentialMeta {
  id: string;
  name: string;
  label: string;
  provider: CredentialProvider;
  kind: CredentialKind;
  scope: CredentialScope;
  /** Never the value. Present so a UI can show *that* it exists, not what. */
  present: true;
  createdAt: number;
  updatedAt: number;
}

export interface StoredCredential extends CredentialMeta {
  ciphertext: string;
  iv: string;
  salt: string;
  algorithm: string;
  /** F 145 D — the key schedule that sealed it. */
  kdf: KdfId;
  description?: string;
}

// ── storage seam ───────────────────────────────────────────────────────────

/**
 * Pluggable backing store. IndexedDB in the app; a Map in tests and on a
 * server. The credential store must not assume a browser.
 */
export interface CredentialStorage {
  get(id: string): Promise<StoredCredential | undefined>;
  put(rec: StoredCredential): Promise<void>;
  delete(id: string): Promise<void>;
  list(): Promise<StoredCredential[]>;
}

/** In-memory storage. The default, and what tests use. */
export function createMemoryCredentialStorage(
  seed: StoredCredential[] = [],
): CredentialStorage {
  const map = new Map(seed.map((r) => [r.id, r]));
  return {
    async get(id) { return map.get(id); },
    async put(rec) { map.set(rec.id, rec); },
    async delete(id) { map.delete(id); },
    async list() { return [...map.values()]; },
  };
}

// ── key handling ───────────────────────────────────────────────────────────

/**
 * Unsealing.
 *
 * The store never derives a key itself: the CALLER supplies one, already
 * derived. A credential store that also held the passphrase would be a
 * password manager that keeps its own password in reach, and it would give
 * `resolve()` no boundary to enforce.
 *
 * For the session-key case the caller passes
 * `getSessionKeyBytes()` from `freshcards/credentials`.
 */
/**
 * F 421 — THE RESOLVER NOW RECEIVES THE STORED SALT.
 *
 * The store records a per-credential random salt at `set()` time, and that is the
 * salt that drove the encryption. The unseal path (`resolve()`) hands that same
 * `rec.salt` back to the resolver. Without this second parameter, the resolver
 * had to derive its own salt — typically `passphraseSalt(principalId)`, a
 * deterministic label — and the stored salt was a placebo that the bridge
 * (which only knows the stored salt) could never reproduce.
 *
 * The resolver MAY still ignore `saltB64` and derive its own (for example an
 * env-secret-backed resolver is salted by variable name, not by record). This
 * parameter is a *hint*, and a resolver that knows the right salt for itself
 * may use it; a resolver that does not may use what it has.
 */
export type KeyResolver = (
  principal: Principal,
  saltB64?: string,
) => Promise<string> | string;

/** Derive a key from a passphrase for a given record. */
export function keyFromPassphrase(
  passphrase: string,
  salt: string,
  kdf: KdfId = 'pbkdf2',
): Promise<string> {
  return deriveKeyWithKdf(passphrase, salt, kdf);
}

// ── the store ──────────────────────────────────────────────────────────────

export interface CredentialStoreOptions {
  storage?: CredentialStorage;
  resolveKey: KeyResolver;
  /** Fallback principal when a call does not name one. */
  defaultPrincipal?: Principal;
  /**
   * Ambient principal — the widest one, used only if nothing else applies.
   * Deliberately opt-in and deliberately last.
   */
  ambientPrincipal?: Principal;
  /**
   * F 425 — DECLARE THE SALT, INSTEAD OF GUESSING ONE PER RECORD.
   *
   * The store mints a random salt per record and asks the resolver to derive
   * from it. That is correct for a resolver that DERIVES (a passphrase), and
   * wrong for one that RETURNS a fixed key (the restored session key): the node
   * would then carry a salt no passphrase can reproduce, and every third-party
   * reader fails — which is the F 421 defect wearing a different hat.
   *
   * A caller whose resolver returns a pre-derived key supplies `saltFor`, and
   * the store records THAT salt on the node. Absent it, behaviour is unchanged
   * and stays byte-identical to F 421.
   */
  saltFor?: (principal: Principal) => string | Promise<string>;
}

export interface SetInput {
  name: string;
  label: string;
  provider: CredentialProvider;
  kind: CredentialKind;
  value: string;
  description?: string;
  principal?: Principal;
}

/**
 * F 328 — the canonical credential id.
 *
 * The provider is part of the identity, not a label on it: two providers may
 * legitimately hold the same credential name under one principal, and an id
 * that cannot tell them apart cannot store both.
 */
const qualify = (p: Principal, provider: CredentialProvider, name: string): string =>
  `${p.scope}:${p.id}:${provider}:${name}`;

/**
 * The legacy shape was `${scope}:${id}:${name}` — three segments. The new shape
 * is four. Counting segments is the whole test, and it is deliberately made on
 * the RECORD's own fields rather than on a re-parse of its id, because a
 * principal `id` or a credential `name` is caller-supplied and could itself
 * contain a colon. A record is legacy iff its id does not equal the canonical
 * id implied by the fields it already carries.
 */
function isLegacyId(rec: StoredCredential): boolean {
  const scope = rec.scope as CredentialScope;
  const principal: Principal = { scope, id: principalIdOf(rec) };
  return rec.id !== qualify(principal, rec.provider, rec.name);
}

/** Recover the principal `id` segment from a record's own id. */
function principalIdOf(rec: StoredCredential): string {
  // scope is the first segment and never contains a colon; the principal id is
  // everything up to the segment before the final two (provider, name) for a
  // canonical id, or before the final one for a legacy id. Both are handled by
  // matching against the name/provider the record actually stores.
  const rest = rec.id.slice(`${rec.scope}:`.length);
  const tail = `:${rec.name}`;
  const providerIdx = rec.provider ? `:${rec.provider}` : '';
  const cut = providerIdx && rest.endsWith(`${providerIdx}${tail}`)
    ? rest.length - providerIdx.length - tail.length
    : rest.length - tail.length;
  return cut > 0 ? rest.slice(0, cut) : rest;
}

const OPERATOR = (id: string): Principal => ({ scope: 'operator', id });

/** The operator's own principal — the common case for a self-hosted brain. */
export function operatorPrincipal(id: string): Principal { return OPERATOR(id); }

export class CredentialStore {
  private readonly storage: CredentialStorage;
  private readonly resolveKey: KeyResolver;
  private readonly defaultPrincipal?: Principal;
  private readonly ambientPrincipal?: Principal;
  private readonly saltFor?: (principal: Principal) => string | Promise<string>;

  constructor(opts: CredentialStoreOptions) {
    this.storage = opts.storage ?? createMemoryCredentialStorage();
    this.resolveKey = opts.resolveKey;
    this.defaultPrincipal = opts.defaultPrincipal;
    this.ambientPrincipal = opts.ambientPrincipal;
    this.saltFor = opts.saltFor;
  }

  /** Metadata only. This is what a consumer-facing API returns. */
  async list(principal?: Principal): Promise<CredentialMeta[]> {
    const all = await this.storage.list();
    const scoped = principal ? all.filter((r) => this.belongsTo(r, principal)) : all;
    return scoped.map((r) => toMeta(r));
  }

  /** Metadata only — never the value. */
  async get(input:
    | { id: string }
    | { name: string; provider?: CredentialProvider; principal?: Principal }
  ): Promise<CredentialMeta | null> {
    const rec = await this.resolveRecord(input);
    return rec ? toMeta(rec) : null;
  }

  /**
   * F 390 — THE SEALED RECORD, for a consumer that has to MOVE it.
   *
   * `get()` returns METADATA and never the payload, and that boundary is the
   * point of the module: `resolve()` is the only function here that returns a
   * secret, and it is internal by construction.
   *
   * But the credential's canonical home is a graph NODE, and a node is built
   * from the record — the F 327 test reaches into the storage for exactly
   * this. So the store needs to hand the record over to a caller that is
   * going to publish the CIPHERTEXT.
   *
   * WHAT THIS RETURNS: `ciphertext`, `iv`, `salt`, `algorithm`, `kdf` and the
   * metadata. The PLAINTEXT is not in a `StoredCredential` and cannot be
   * recovered from it — it would take the key. So this widens the boundary
   * from "no payload" to "no PLAINTEXT", which is the boundary the graph
   * actually needs.
   */
  async getStored(id: string): Promise<StoredCredential | null> {
    const rec = await this.storage.get(id);
    return rec ?? null;
  }

  async set(input: SetInput): Promise<CredentialMeta> {
    const principal = input.principal ?? this.defaultPrincipal ?? this.ambientPrincipal;
    if (!principal) {
      throw new Error(
        '[credentials] no principal. A credential with no owner is not a credential, ' +
        'it is a secret in a list. Pass principal, or configure defaultPrincipal.'
      );
    }
    if (!input.value) throw new Error('[credentials] value must not be empty');

    // F 425 — THE SALT THE KEY WAS ACTUALLY DERIVED FROM.
    //
    // Default: a fresh random salt, and the resolver derives from it (F 421's
    // honest-salt contract). With `saltFor`, the caller is sealing under a
    // PRE-DERIVED key, so the salt must be the one behind that key — a random
    // one here would make the record unreadable to anyone holding a passphrase.
    const salt = this.saltFor ? await this.saltFor(principal) : randomSalt();
    const key = await this.resolveKey(principal, salt);
    const enc = await encryptWithKeyFor(input.value, key, salt);

    const id = qualify(principal, input.provider, input.name);
    const now = Date.now();
    // F 328 — MOVE a legacy record, do not shadow it.
    //
    // Writing the new-format id while a legacy record for the same
    // (principal, name, provider) is still present would leave TWO logical
    // credentials under two different keys: the write succeeds, the old one
    // survives, and `list()` shows a duplicate that nothing ever removes. So the
    // legacy key is adopted — `createdAt` carries across — and the old key is
    // deleted in the same call that writes the new one.
    const legacy = await this.findByFields(principal, input.name, input.provider, id);
    const prior = legacy ?? (await this.storage.get(id));
    if (legacy && legacy.id !== id) await this.storage.delete(legacy.id);
    const rec: StoredCredential = {
      id,
      name: input.name,
      label: input.label,
      provider: input.provider,
      kind: input.kind,
      scope: principal.scope,
      present: true,
      createdAt: prior?.createdAt ?? now,
      updatedAt: now,
      description: input.description,
      ...enc,
    };
    await this.storage.put(rec);
    return toMeta(rec);
  }

  async delete(input:
    | { id: string }
    | { name: string; provider?: CredentialProvider; principal?: Principal }
  ): Promise<boolean> {
    const rec = await this.resolveRecord(input);
    if (!rec) return false;
    await this.storage.delete(rec.id);
    return true;
  }

  /**
   * THE PLAINTEXT BOUNDARY.
   *
   * This is the only function in the module that returns a secret, and it is
   * internal by construction: it requires a principal, and a consumer API
   * that only ever sees `CredentialMeta` cannot reach it.
   *
   * Resolution order: an EXPLICIT principal is exclusive — only that
   * principal is searched, and a miss returns null rather than falling back.
   * With no explicit principal, default is tried, then ambient.
   */
  async resolve(input: {
    provider?: CredentialProvider;
    name?: string;
    principal?: Principal;
  }): Promise<string | null> {
    const candidates = this.candidatePrincipals(input.principal);
    for (const p of candidates) {
      const rec = await this.findRecord(input.provider, input.name, p);
      if (!rec) continue;
      const key = await this.resolveKey(p, rec.salt);
      try {
        return await decryptWithKeyFor(rec, key);
      } catch {
        // Right record, wrong key for this principal. Keep looking rather than
        // returning a value that decrypted to nothing.
        continue;
      }
    }
    return null;
  }

  /**
   * An EXPLICIT principal is EXCLUSIVE. F 146 B.
   *
   * The clean-consumer test caught this: asking for provider `groq` as
   * principal `{scope:'user', id:'u'}` fell through to the configured default
   * (the operator) and returned the OPERATOR'S SECRET.
   *
   * That is the wrong default, and it is a security property rather than a
   * convenience. An app asking "give me user X's key" and silently receiving
   * someone else's is a privilege escalation wearing a fallback's clothes —
   * the caller cannot tell the difference between "resolved" and "resolved
   * for the wrong principal".
   *
   * So: if the caller names a principal, ONLY that principal is searched. The
   * default and ambient apply only when nobody named anyone.
   */
  private candidatePrincipals(explicit?: Principal): Principal[] {
    if (explicit) return [explicit];
    const out: Principal[] = [];
    if (this.defaultPrincipal) out.push(this.defaultPrincipal);
    if (this.ambientPrincipal && !out.some((p) => samePrincipal(p, this.ambientPrincipal!))) {
      out.push(this.ambientPrincipal);
    }
    return out;
  }

  private async findRecord(
    provider: CredentialProvider | undefined,
    name: string | undefined,
    p: Principal,
  ): Promise<StoredCredential | undefined> {
    if (name) {
      // F 328 — when a provider is named the id is computable, so try it
      // directly; a miss falls through to the field scan below, which is what
      // keeps a legacy-format record reachable.
      if (provider) {
        const byId = await this.storage.get(qualify(p, provider, name));
        if (byId && byId.provider === provider) return byId;
      }
      return this.findByFields(p, name, provider);
    }
    const all = await this.storage.list();
    return all.find(
      (r) => this.belongsTo(r, p) && (!provider || r.provider === provider),
    );
  }

  /**
   * F 328 — FIELD-BASED fallback, the reason a format change is survivable.
   *
   * Matches on what a record actually stores (`scope`, `name`, `provider`) rather
   * than on the shape of its id, so it finds legacy records whose id is three
   * segments and canonical ones whose id is four, in the same pass. `excludeId`
   * lets `set()` use it to find a legacy key that must be adopted.
   */
  private async findByFields(
    p: Principal,
    name: string,
    provider: CredentialProvider | undefined,
    excludeId?: string,
  ): Promise<StoredCredential | undefined> {
    const all = await this.storage.list();
    return all.find(
      (r) =>
        r.id !== excludeId &&
        this.belongsTo(r, p) &&
        r.name === name &&
        (!provider || r.provider === provider),
    );
  }

  private async resolveRecord(
    input: { id: string } | { name: string; provider?: CredentialProvider; principal?: Principal },
  ): Promise<StoredCredential | undefined> {
    // `'id' in input` is not enough: the second member declares `id` as
    // optional-free, so TS will not narrow the union on key presence alone.
    // Test the VALUE, which is what actually decides the branch.
    const direct = (input as { id?: string }).id;
    if (direct) return this.storage.get(direct);
    const p = (input as { principal?: Principal }).principal ?? this.defaultPrincipal;
    if (!p) return undefined;
    const name = (input as { name: string }).name;
    const provider = (input as { provider?: CredentialProvider }).provider;
    // F 328 — without a provider the id is not computable, so this is always a
    // field scan. That is the old behaviour's shape and it is also what keeps a
    // legacy-format record reachable from a name-only lookup.
    return this.findByFields(p, name, provider);
  }

  /**
   * F 328 — EXPLICIT, IDEMPOTENT MIGRATION.
   *
   * Rewrites every legacy three-segment id to the canonical four-segment id.
   * Runs once, or safely many times: a record already carrying its canonical id
   * is left alone, so a second pass is a no-op and reports zero migrations.
   *
   * It is deliberately NOT called on construction. A store that migrates on
   * construction performs a write during construction, which means a read-only
   * consumer — or a store built in a test harness that never asked for a write —
   * would mutate storage it was only meant to observe. The caller decides when.
   *
   * `createdAt`/`updatedAt` are preserved, and the ciphertext is copied
   * untouched: only the key changes, so a migrated record still decrypts with the
   * key its principal derives.
   */
  async migrateCredentialIds(): Promise<{ migrated: number; ids: string[] }> {
    const all = await this.storage.list();
    const ids: string[] = [];
    for (const rec of all) {
      if (!isLegacyId(rec)) continue;
      const principal: Principal = { scope: rec.scope as CredentialScope, id: principalIdOf(rec) };
      const next = qualify(principal, rec.provider, rec.name);
      if (next === rec.id) continue;
      // Never clobber a canonical record that already owns the target id.
      const clash = await this.storage.get(next);
      if (clash) continue;
      await this.storage.put({ ...rec, id: next });
      await this.storage.delete(rec.id);
      ids.push(`${rec.id} -> ${next}`);
    }
    return { migrated: ids.length, ids };
  }

  private belongsTo(r: StoredCredential, p: Principal): boolean {
    return r.scope === p.scope && r.id.startsWith(`${p.scope}:${p.id}:`);
  }
}

function samePrincipal(a: Principal, b: Principal): boolean {
  return a.scope === b.scope && a.id === b.id;
}

function toMeta(r: StoredCredential): CredentialMeta {
  return {
    id: r.id,
    name: r.name,
    label: r.label,
    provider: r.provider,
    kind: r.kind,
    scope: r.scope,
    present: true,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
  };
}

// ── crypto plumbing (session-key path, no passphrase on the hot path) ──────

function randomSalt(): string {
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  return btoa(String.fromCharCode(...b));
}

/** Encrypt under an already-derived key, recording which KDF sealed it. */
async function encryptWithKeyFor(
  value: string,
  keyB64: string,
  salt: string,
): Promise<Pick<StoredCredential, 'ciphertext' | 'iv' | 'salt' | 'algorithm' | 'kdf'>> {
  const iv = (() => {
    const b = new Uint8Array(12);
    crypto.getRandomValues(b);
    return btoa(String.fromCharCode(...b));
  })();
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    b64ToBuf(keyB64),
    { name: 'AES-GCM' },
    false,
    ['encrypt'],
  );
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: b64ToBuf(iv) },
    cryptoKey,
    new TextEncoder().encode(value),
  );
  return {
    ciphertext: bufToB64(ct),
    iv,
    salt,
    algorithm: 'AES-GCM',
    // F 145 D — record the key schedule that actually sealed it. Here the key
    // is supplied by the caller, so the honest answer is "not derived here".
    kdf: 'pbkdf2',
  };
}

async function decryptWithKeyFor(rec: StoredCredential, keyB64: string): Promise<string> {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    b64ToBuf(keyB64),
    { name: 'AES-GCM' },
    false,
    ['decrypt'],
  );
  const pt = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: b64ToBuf(rec.iv) },
    cryptoKey,
    b64ToBuf(rec.ciphertext),
  );
  return new TextDecoder().decode(pt);
}

function b64ToBuf(b64: string): ArrayBuffer {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

function bufToB64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

export { encrypt, decrypt };
